import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH } from '@construction/shared';
import { authApi } from '@/api/endpoints';
import { setTokens } from '@/api/tokens';
import { Alert, BackLink, Button, Card, Field, Input } from '@/components/ui';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { useMobileShell } from '@/hooks/useMobileShell';
import { ROUTE_BY_ID } from '@/lib/routes/registry';
import { ROUTE_LABEL_KEY } from '@/lib/routes/route-labels';
import { useAuthStore } from '@/store/auth';

// 返回键去哪由路由表决定（CR-044），这里不另写一份
const backParent = ROUTE_BY_ID.get('changePassword')?.parent;
const backRoute = backParent ? ROUTE_BY_ID.get(backParent) : undefined;

// 和服务端同一份（CR-077 起在 shared）
const MIN_LENGTH = PASSWORD_MIN_LENGTH;

export function ChangePasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const toMessage = useErrorMessage();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const firstLogin = user?.mustChangePassword ?? false;
  const isMobileShell = useMobileShell();

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    // 本地先挡一道，省得为了两次输入不一致跑一趟服务器
    if (next.length < MIN_LENGTH) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    if (next !== confirm) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setBusy(true);
    try {
      // 服务端改密时会把旧会话全部作废，这里必须换上它返回的新 token，
      // 否则下一个请求就会被判成「会话已失效」踢回登录页
      const tokens = await authApi.changePassword(current, next);
      setTokens({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
      if (user) setUser({ ...user, mustChangePassword: false });
      // 用哪套外壳由设备决定，不看权限（CR-040）：交给首页重定向去分
      navigate('/', { replace: true });
    } catch (err) {
      setError(toMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="safe-top safe-bottom flex min-h-full flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-sm">
        {/*
          这一页挂在两套外壳之外（首次登录强制改密也走这里），MobileLayout 画不到它，自己画返回键（CR-044）。
          App 里回路由表写的上一级（我的）；网页端回首页，由首页重定向分到落脚点。
          首次登录不给返回：改完才能用。
        */}
        {!firstLogin && (
          <BackLink
            to={isMobileShell && backRoute ? backRoute.path : '/'}
            label={t('app.backTo', {
              name: isMobileShell && backRoute ? t(ROUTE_LABEL_KEY[backRoute.id]) : t('nav.home'),
            })}
          />
        )}
        <h1 className="text-heading font-semibold tracking-tight">{t('auth.changePasswordTitle')}</h1>
        {firstLogin && (
          <p className="mt-1.5 text-body leading-relaxed text-ink-3">
            {t('auth.changePasswordFirstLogin')}
          </p>
        )}

        {/* 和登录页一样：浅灰底上一张白卡（CR-082） */}
        <Card className="mt-6 p-6 shadow-sm">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {error && <Alert>{error}</Alert>}

            <Field label={t('auth.currentPassword')} htmlFor="current" required>
              <Input
                id="current"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="off"
                required
              />
            </Field>

            <Field label={t('auth.newPassword')} htmlFor="next" required hint={t('auth.passwordTooShort')}>
              <Input
                id="next"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="off"
                minLength={MIN_LENGTH}
                required
              />
            </Field>

            <Field label={t('auth.confirmPassword')} htmlFor="confirm" required>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="off"
                required
              />
            </Field>

            <Button type="submit" block loading={busy} className="mt-2">
              {t('auth.changePassword')}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
